import { useState, useCallback } from 'react'
import { IconFileDescription } from '@tabler/icons-react'

import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../atoms/dialog'
import { PrimaryButton } from '../../atoms/buttons/PrimaryButton'
import { SecondaryButton } from '../../atoms/buttons/SecondaryButton'
import Typography from '../../atoms/Typography'
import { api } from 'src/api/root'

const MAX_LENGTH = 2_000

interface EditLayerDescriptionDialogProps {
  id: string
  description: string | null
}

const EditLayerDescriptionDialog = ({ id, description }: EditLayerDescriptionDialogProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false)
  const [value, setValue] = useState(description ?? '')

  const { mutateAsync, isPending } = api.layer.updateDescription.useMutation()

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  const handleSubmit = useCallback(async () => {
    await mutateAsync({
      fileId: id,
      description: value.trim() || null,
    })
    setIsOpen(false)
  }, [value, id, mutateAsync])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open)
      if (open) {
        setValue(description ?? '')
      }
    },
    [description],
  )

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger className="flex h-11 w-11 items-center justify-center rounded-[8px] text-light-green-500 transition-all hover:cursor-pointer hover:text-white active:scale-95">
        <IconFileDescription size={24} />
      </DialogTrigger>

      <DialogContent className="w-[480px]" aria-description="edit-layer-description-dialog">
        <DialogHeader aria-describedby="edit-layer-description-dialog">
          <DialogTitle>
            <Typography variant="xl">Edit Description</Typography>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-1">
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value.slice(0, MAX_LENGTH))}
            placeholder="Enter description"
            rows={8}
            disabled={isPending}
            className="w-full resize-y rounded-md border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-light-green-500 focus:outline-none disabled:opacity-50"
          />
          <span className={`text-xs ${value.length >= MAX_LENGTH ? 'text-red-400' : 'text-gray-500'}`}>
            {value.length}/{MAX_LENGTH}
          </span>
        </div>

        <DialogFooter className="sm:justify-center sm:space-x-6">
          <SecondaryButton isLoading={isPending} onClick={handleClose} className="w-40">
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={handleSubmit} isLoading={isPending} className="w-40">
            Save
          </PrimaryButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default EditLayerDescriptionDialog
